import {Network} from "../../../network/Network";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {ProductType} from "../../../models/product/ProductType";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {ICommunityLibraryMediaModel} from "../../../models/community/library/ICommunityLibraryMediaModel";
import {ICommunityLibraryMediasModel} from "../../../models/community/library/ICommunityLibraryMediasModel";
import {ICommunityLibraryFolderModel} from "../../../models/community/library/ICommunityLibraryFolderModel";
import {ICommunityLibraryReferenceModel} from "../../../models/community/library/ICommunityLibraryReferenceModel";
import {ICommunityLibraryImportEndModel} from "../../../models/community/library/ICommunityLibraryImportEndModel";
import {ICommunityLibrarySiteImportItemModel} from "../../../models/community/library/ICommunityLibrarySiteImportItemModel";
import {ICommunityLibraryMediaApplyModel} from "../../../models/community/library/ICommunityLibraryMediaApplyModel";
import {ICommunityLibraryModelModel} from "../../../models/community/library/ICommunityLibraryModelModel";
import {ICommunityLibraryCreditsModel} from "../../../models/community/library/ICommunityLibraryCreditsModel";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {Services} from "../../Services";
import {ErrorCode} from "../../../network/status/error/ErrorCode";
import {INetworkComponentError} from "../../../network/types/INetworkComponentError";
import {Resources} from "../../../resources/Resources";

const STORAGE_LIMIT_ERROR: INetworkComponentError = {
    errorCode: ErrorCode.SIZE_MAX,
    get message() {
        return Resources.t("words.mediaLibrary.storageLimitReached");
    }
};

const IMPORT_IN_PROGRESS_ERROR: INetworkComponentError = {
    errorCode: ErrorCode.TASK_IN_PROGRESS,
    get message() {
        return Resources.t("words.mediaLibrary.importInProgress");
    }
};

export class CommunityLibraryService {

    private static mediaUpdateEvents: ((media: ICommunityLibraryMediaModel) => void)[] = [];
    private static modelsCache: ICommunityLibraryModelModel[];
    private static importEndEvents: ((result: ICommunityLibraryImportEndModel) => void)[] = [];

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_LIBRARY_MEDIA_UPDATE, (data) => {
            this.mediaUpdateEvents.forEach(callback => callback(data));
        });
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_LIBRARY_IMPORT_END, (data) => {
            this.importEndEvents.forEach(callback => callback(data));
        });
    }

    public static onMediaUpdateEvent(component: INetworkComponent, func: (media: ICommunityLibraryMediaModel) => void) {
        this.mediaUpdateEvents.push(func);
        component.onRemove(() => arrayRemove(this.mediaUpdateEvents, func));
    }

    public static onImportEndEvent(component: INetworkComponent, func: (result: ICommunityLibraryImportEndModel) => void) {
        this.importEndEvents.push(func);
        component.onRemove(() => arrayRemove(this.importEndEvents, func));
    }

    public static async medias(communityId: string, cursor?: string, folderId?: string, source?: string, query?: string, accountIds?: string[], component?: INetworkComponent): Promise<ICommunityLibraryMediasModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/list", {
            communityId: communityId,
            ...(cursor ? {cursor: cursor} : {}),
            ...(folderId ? {folderId: folderId} : {}),
            ...(source && source !== "all" ? {source: source} : {}),
            ...(query ? {query: query} : {}),
            ...(accountIds && accountIds.length > 0 ? {accountIds: accountIds.join(",")} : {})
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async get(communityId: string, mediaId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        const request = await Network.get(ProductType.COMMUNITY, `/community/library/medias/get?communityId=${communityId}&mediaId=${mediaId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async upload(communityId: string, folderId: string, files: File[], component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR]);

        const request = await Network.postFormData(ProductType.COMMUNITY, "/community/library/medias/upload", {
            communityId: communityId,
            ...(folderId ? {folderId: folderId} : {}),
            files: files
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async uploadReferences(communityId: string, files: File[], component?: INetworkComponent): Promise<ICommunityLibraryReferenceModel[]> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR]);

        const request = await Network.postFormData(ProductType.COMMUNITY, "/community/library/references/upload", {
            communityId: communityId,
            files: files
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async models(component?: INetworkComponent): Promise<ICommunityLibraryModelModel[]> {
        if (this.modelsCache) {
            return this.modelsCache;
        }

        const request = await Network.get(ProductType.COMMUNITY, "/community/library/models", component);
        if (request.status == HttpStatus.OK) {
            this.modelsCache = request.data;
            return this.modelsCache;
        }
        return [];
    }

    public static async sourceUrls(communityId: string, component?: INetworkComponent): Promise<string[]> {
        const request = await Network.get(ProductType.COMMUNITY, `/community/library/medias/sourceUrls?communityId=${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async credits(communityId: string, component?: INetworkComponent): Promise<ICommunityLibraryCreditsModel> {
        const request = await Network.get(ProductType.COMMUNITY, `/community/library/credits?communityId=${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async generate(communityId: string, folderId: string, prompt: string, model?: string, format?: string, resolution?: string, referenceFileIds?: string[], component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR]);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/generate", {
            communityId: communityId,
            folderId: folderId,
            prompt: prompt,
            model: model,
            format: format,
            resolution: resolution,
            referenceFileIds: referenceFileIds
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async iterate(communityId: string, mediaId: string, prompt: string, mode?: string, model?: string, referenceFileIds?: string[], component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR]);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/iterate", {
            communityId: communityId,
            mediaId: mediaId,
            prompt: prompt,
            mode: mode,
            model: model,
            referenceFileIds: referenceFileIds
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async productMedias(communityId: string, productId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediasModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/list", {
            communityId: communityId,
            productId: productId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async importProduct(communityId: string, remoteId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediasModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/importProduct", {
            communityId: communityId,
            remoteId: remoteId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async uploadProduct(communityId: string, productId: string, files: File[], component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR]);

        const request = await Network.postFormData(ProductType.COMMUNITY, "/community/library/medias/upload", {
            communityId: communityId,
            productId: productId,
            files: files
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async importProducts(communityId: string, remoteIds: string[], component?: INetworkComponent): Promise<boolean> {
        Services.handleErrors(component, [IMPORT_IN_PROGRESS_ERROR]);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/importProducts", {
            communityId: communityId,
            remoteIds: remoteIds
        }, component);

        return request.status == HttpStatus.OK;
    }

    public static async importSite(communityId: string, folderId: string, items: ICommunityLibrarySiteImportItemModel[], productId?: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR, IMPORT_IN_PROGRESS_ERROR]);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/importSite", {
            communityId: communityId,
            folderId: folderId,
            ...(productId ? {productId: productId} : {}),
            items: items
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async adopt(communityId: string, articleId: string, url: string, alt: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR]);

        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/adopt", {
            communityId: communityId,
            articleId: articleId,
            url: url,
            ...(alt ? {alt: alt} : {})
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async apply(communityId: string, mediaId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaApplyModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/apply", {
            communityId: communityId,
            mediaId: mediaId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async productOrder(communityId: string, productId: string, mediaIds: string[], component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/productOrder", {
            communityId: communityId,
            productId: productId,
            mediaIds: mediaIds
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async publish(communityId: string, mediaId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/publish", {
            communityId: communityId,
            mediaId: mediaId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async attachToProduct(communityId: string, productId: string, mediaIds: string[], component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        Services.handleErrors(component, [STORAGE_LIMIT_ERROR]);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/attachToProduct", {
            communityId: communityId,
            productId: productId,
            mediaIds: mediaIds
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async publishProduct(communityId: string, productId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/publishProduct", {
            communityId: communityId,
            productId: productId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async altUpdate(communityId: string, mediaId: string, alt: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/altUpdate", {
            communityId: communityId,
            mediaId: mediaId,
            alt: alt
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async altGenerate(communityId: string, mediaId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/altGenerate", {
            communityId: communityId,
            mediaId: mediaId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async altGenerateProduct(communityId: string, productId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/altGenerateProduct", {
            communityId: communityId,
            productId: productId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async selectVersion(communityId: string, mediaId: string, versionId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/selectVersion", {
            communityId: communityId,
            mediaId: mediaId,
            versionId: versionId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async duplicate(communityId: string, mediaId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/duplicate", {
            communityId: communityId,
            mediaId: mediaId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async move(communityId: string, mediaIds: string[], folderId: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel[]> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/move", {
            communityId: communityId,
            mediaIds: mediaIds,
            folderId: folderId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async rename(communityId: string, mediaId: string, name: string, component?: INetworkComponent): Promise<ICommunityLibraryMediaModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/medias/rename", {
            communityId: communityId,
            mediaId: mediaId,
            name: name
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async deleteMedias(communityId: string, mediaIds: string[], component?: INetworkComponent): Promise<boolean> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/library/medias/delete", {
            communityId: communityId,
            mediaIds: mediaIds
        }, component);

        return request.status == HttpStatus.OK;
    }

    public static async folders(communityId: string, component?: INetworkComponent): Promise<ICommunityLibraryFolderModel[]> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/folders/list", {
            communityId: communityId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async folderCreate(communityId: string, name: string, color: string, accent: string, component?: INetworkComponent): Promise<ICommunityLibraryFolderModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/folders/create", {
            communityId: communityId,
            name: name,
            color: color,
            accent: accent
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async folderUpdate(communityId: string, folderId: string, name?: string, color?: string, accent?: string, component?: INetworkComponent): Promise<ICommunityLibraryFolderModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/folders/update", {
            communityId: communityId,
            folderId: folderId,
            name: name,
            color: color,
            accent: accent
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async folderDelete(communityId: string, folderId: string, component?: INetworkComponent): Promise<boolean> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/library/folders/delete", {
            communityId: communityId,
            folderId: folderId
        }, component);

        return request.status == HttpStatus.OK;
    }
}
